"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/lib/analytics";
import { storyblokEditable } from "@storyblok/react/rsc";

import { useMembership } from "@/lib/hooks/useMembership";
import { ArrowRightThin } from "@/components/icons";
import { ArticleTag } from "@/components/layout/ArticleTag";
import { GridContainer } from "@/components/layout/GridContainer";
import { ArticleTitle } from "@/components/layout/ArticleCard";
import { ArticlePreviewModal, type ArticlePreviewData } from "@/components/landing/ArticlePreviewModal";
import { useIsLandingPage } from "@/lib/landing/LandingContext";
import type { ArticleStoryblok } from "@/component-types-sb";
import { fetchStoryblokList } from "@/lib/storyblok/fetchList";

type SingleArticleCardStoryblok = {
  title?: string;
  article?: string | { content: any; full_slug: string; [k: string]: any };
  tags?: (number | string)[];
  button_label?: string;
  _uid: string;
  component: "single_article_card";
  [k: string]: any;
};

type SingleArticleCardProps = {
  blok: SingleArticleCardStoryblok;
};

type ArticleData = {
  title: string;
  image?: { src: string; alt: string };
  tags: string[];
  href: string;
  memberOnly: boolean;
  subtitle?: string;
  excerpt?: string;
};

function parseArticleStory(story: { content: any; full_slug: string }): ArticleData {
  const content = story.content as ArticleStoryblok;
  return {
    title: content.title,
    image: content.image?.filename
      ? { src: content.image.filename, alt: content.image.alt || content.title }
      : undefined,
    tags: (content.tags || []).map(String),
    href: `/${story.full_slug}`,
    memberOnly: content.member_access === "member_only",
    subtitle: content.subtitle,
    excerpt: (content as any).except,
  };
}

const SingleArticleCard = ({ blok }: SingleArticleCardProps) => {
  const isLanding = useIsLandingPage();
  const [previewArticle, setPreviewArticle] = useState<ArticlePreviewData | null>(null);

  const [article, setArticle] = useState<ArticleData | null>(() => {
    // Use resolved article from server if available
    if (blok.article && typeof blok.article !== "string" && "content" in blok.article) {
      return parseArticleStory(blok.article);
    }
    return null;
  });

  const tags = blok.tags;

  // Fetch latest article by tags (only if no article is pre-resolved)
  const hasResolvedArticle = blok.article && typeof blok.article !== "string" && "content" in blok.article;
  useEffect(() => {
    if (hasResolvedArticle) return;
    if (!tags || tags.length === 0) return;

    const fetchArticle = async () => {
      try {
        const tagStrings = tags.map(String);
        const stories = await fetchStoryblokList({
          content_type: "article",
          filter_query: {
            tags: { any_in_array: tagStrings.join(",") },
          },
          sort_by: "first_published_at:desc",
          per_page: 10,
        });

        if (stories && stories.length > 0) {
          // Favor articles with the most matching tags
          const tagSet = new Set(tagStrings);
          const ranked = stories
            .map((story: any) => ({
              story,
              matchCount: ((story.content as ArticleStoryblok).tags || [])
                .filter((t: number | string) => tagSet.has(String(t))).length,
            }))
            .sort((a: any, b: any) => b.matchCount - a.matchCount);
          setArticle(parseArticleStory(ranked[0].story));
        }
      } catch (error) {
        console.error("Error fetching article:", error);
      }
    };

    fetchArticle();
  }, [tags]);

  const { isMember } = useMembership();
  const pathname = usePathname();
  const isHub = pathname.startsWith("/reviews/");

  const handleClick = () => {
    if (isHub && article) {
      trackEvent("hub_article_clicked", {
        article_title: article.title,
        article_url: article.href,
        hub: pathname,
      });
    }
  };

  if (!article) return null;

  const ctaLabel = blok.button_label || "Artikel lesen";

  // Textlink mit Pfeil wie "Zum Event" auf der EventCard. Ein `<span>`, kein
  // `<Link>`: die ganze Karte ist schon ein Link, und Links duerfen nicht
  // verschachtelt werden. Der Hover kommt ueber `group` von der Karte.
  const readLink = (
    <span className="inline-flex items-center [&>*+*]:ml-2 text-copy text-black group-hover:opacity-70 transition-opacity">
      <span>{ctaLabel}</span>
      <ArrowRightThin className="size-4" />
    </span>
  );

  const openPreview = () => {
    setPreviewArticle({
      title: article.title,
      image: article.image,
      tags: article.tags,
      memberOnly: article.memberOnly,
      subtitle: article.subtitle,
      excerpt: article.excerpt,
    });
    trackEvent("content_preview_opened", {
      type: "article",
      title: article.title,
      source: "single_article_card",
    });
  };

  return (
    <GridContainer {...storyblokEditable(blok)} className="!px-0 mb-4 md:mb-6">
      <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 px-4 md:px-0 flex flex-col gap-4 items-center">
        {blok.title && (
          <h2 className="text-headline-xl text-black text-center">
            {blok.title}
          </h2>
        )}
        {isLanding ? (
          <div className="bg-white rounded-3xl p-3 flex flex-col gap-6 w-full card-rotate cursor-pointer" onClick={openPreview} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && openPreview()}>
          {/* Mobile: stacked layout */}
          <div className="flex flex-col gap-4 md:hidden">
            <div className="relative w-full h-[167px] rounded-2xl overflow-hidden">
              {article.image ? (
                <Image
                  src={article.image.src}
                  alt={article.image.alt}
                  fill
                  sizes="100vw"
                  className="object-cover"
                />
              ) : (
                <div className="w-full h-full bg-sand-100 rounded-2xl" />
              )}
            </div>
            <div className="flex flex-col gap-3 items-center text-center">
              <ArticleTitle as="p" title={article.title} memberOnly={article.memberOnly} isMember={isMember} className="justify-center" />
              {article.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-center">
                  {article.tags.map((tag) => (
                    <ArticleTag key={tag} label={tag} memberOnly={article.memberOnly} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tablet+: horizontal layout */}
          <div className="hidden md:flex md:flex-row gap-8 items-center">
            <div className="relative w-[240px] h-[167px] rounded-2xl overflow-hidden shrink-0">
              {article.image ? (
                <Image
                  src={article.image.src}
                  alt={article.image.alt}
                  fill
                  sizes="240px"
                  className="object-cover"
                />
              ) : (
                <div className="w-full h-full bg-sand-100 rounded-2xl" />
              )}
            </div>
            <div className="flex flex-col gap-4 items-start text-left flex-1">
              <ArticleTitle as="p" title={article.title} memberOnly={article.memberOnly} isMember={isMember} />
              {article.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {article.tags.map((tag) => (
                    <ArticleTag key={tag} label={tag} memberOnly={article.memberOnly} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        ) : (
          <Link href={article.href} onClick={handleClick} className="group bg-white rounded-3xl p-3 flex flex-col gap-6 w-full card-rotate">
            {/* Mobile: stacked layout */}
            <div className="flex flex-col gap-4 md:hidden">
              <div className="relative w-full h-[167px] rounded-2xl overflow-hidden">
                {article.image ? (
                  <Image
                    src={article.image.src}
                    alt={article.image.alt}
                    fill
                    sizes="100vw"
                    className="object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-sand-100 rounded-2xl" />
                )}
              </div>
              <div className="flex flex-col gap-3 items-center text-center">
                <ArticleTitle as="p" title={article.title} memberOnly={article.memberOnly} isMember={isMember} className="justify-center" />
                {article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 justify-center">
                    {article.tags.map((tag) => (
                      <ArticleTag key={tag} label={tag} memberOnly={article.memberOnly} />
                    ))}
                  </div>
                )}
              </div>
              <span className="self-center">{readLink}</span>
            </div>

            {/* Tablet+: horizontal layout */}
            <div className="hidden md:flex md:flex-row gap-8 items-center">
              <div className="relative w-[240px] h-[167px] rounded-2xl overflow-hidden shrink-0">
                {article.image ? (
                  <Image
                    src={article.image.src}
                    alt={article.image.alt}
                    fill
                    sizes="240px"
                    className="object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-sand-100 rounded-2xl" />
                )}
              </div>
              <div className="flex flex-col gap-4 items-start text-left flex-1">
                <ArticleTitle as="p" title={article.title} memberOnly={article.memberOnly} isMember={isMember} />
                {article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {article.tags.map((tag) => (
                      <ArticleTag key={tag} label={tag} memberOnly={article.memberOnly} />
                    ))}
                  </div>
                )}
                {readLink}
              </div>
            </div>
          </Link>
        )}
        {previewArticle && (
          <ArticlePreviewModal article={previewArticle} onClose={() => setPreviewArticle(null)} />
        )}
      </div>
    </GridContainer>
  );
};

export default SingleArticleCard;
